import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import PageWrapper from '../../components/MainPageWrapper/PageWrapper';
import { APP_URL, ENDPOINT } from '../../constants/common-app.const';
import {
  ClientType,
  ListenerFullRow,
  PaymentMethod,
} from './CourseEnrollment.const';
import {
  ClientForm,
  INITIAL_CLIENT_FORM,
} from './CourseEnrollment.types';
import useGetCourseGroups from '../../hooks/useGetCourseGroups';
import useCreateContract from '../../hooks/useCreateContract';
import useSubmitContract from '../../hooks/useSubmitContract';
import { useAuth } from '../../auth/AuthContext';
import api from '../../utils/api.util';
import toast from '../../utils/toast.util';
import Stepper from './components/Stepper';
import StepFooter from './components/StepFooter';
import ClientDataStep from './components/ClientDataStep';
import ListenersStep from './components/ListenersStep';
import ListenerModal from './components/ListenerModal';
import PreviousListenersModal, { PreviousListener } from './components/PreviousListenersModal';
import GroupAndPaymentStep from './components/GroupAndPaymentStep';
import DoneStep from './components/DoneStep';

import css from './CourseEnrollment.module.css';

const emptyListener = (): ListenerFullRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  fullNameRu: '',
  fullNameBy: '',
  address: '',
  passport: '',
  passportIssueDate: '',
  passportIssuer: '',
  externalEmail: '',
  status: 'not_started',
});

const CourseEnrollment = () => {
  const { isAuthenticated } = useAuth();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const location = useLocation<{ title?: string; priceKopecks?: number; format?: string; duration?: string; existingContractId?: string; allowedClientTypes?: ClientType[] } | null>();
  const courseTitle = location.state?.title ?? 'Загрузка...';
  const priceKopecks = location.state?.priceKopecks ?? 0;
  const courseFormat = location.state?.format;
  const courseDuration = location.state?.duration;
  const existingContractId = location.state?.existingContractId ?? null;
  const allowedClientTypes: ClientType[] = location.state?.allowedClientTypes ?? ['legal', 'ip', 'individual'];

  const [step, setStep] = useState(1);
  const [clientType, setClientType] = useState<ClientType>(
    allowedClientTypes[0] ?? 'legal',
  );
  const [form, setForm] = useState<ClientForm>(INITIAL_CLIENT_FORM);
  const [prevRequisitesId, setPrevRequisitesId] = useState<string | undefined>(
    undefined,
  );
  const [listeners, setListeners] = useState<ListenerFullRow[]>([]);
  const [groupId, setGroupId] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('erip');
  const [consent, setConsent] = useState(false);
  const [contractId, setContractId] = useState<string | null>(null);
  const [contractUuid, setContractUuid] = useState<string | null>(null);
  const [contractNumber, setContractNumber] = useState<string | null>(null);
  const [imnsVkods, setImnsVkods] = useState<string | null>(null);

  const [listenerModalOpen, setListenerModalOpen] = useState(false);
  const [editingListener, setEditingListener] =
    useState<ListenerFullRow | null>(null);
  const [prevListenersOpen, setPrevListenersOpen] = useState(false);
  const [prevListeners, setPrevListeners] = useState<PreviousListener[]>([]);
  const [prevListenersLoading, setPrevListenersLoading] = useState(false);

  const PAYMENT_METHOD_MAP: Record<PaymentMethod, string> = {
    card: 'alfabank',
    erip: 'erip',
    requisites: 'manual',
  };

  const { groups } = useGetCourseGroups({ courseId: id });
  const { createContract } = useCreateContract();
  const { submitContract } = useSubmitContract();

  // Auto-select first group when groups load
  useEffect(() => {
    if (groups.length > 0 && !groupId) {
      setGroupId(groups[0].id);
    }
  }, [groups, groupId]);

  // Pre-fill phone/email from user profile for fresh enrollments
  useEffect(() => {
    if (!isAuthenticated || existingContractId) return;
    api.get(ENDPOINT.PROFILE)
      .then((profile: any) => {
        setForm((prev) => ({
          ...prev,
          email: prev.email || profile.email || '',
          phone: prev.phone || profile.phoneNumber || '',
        }));
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Restore existing draft contract coming from "Мои курсы"
  useEffect(() => {
    if (!existingContractId) return;
    setContractId(existingContractId);
    setStep(2);

    api.getJson(`${ENDPOINT.CONTRACTS}/${existingContractId}`)
      .then((contract: any) => {
        const isoToDisplay = (iso: string | null) =>
          iso ? iso.split('-').reverse().join('.') : '';
        const restored: ListenerFullRow[] = (contract.listeners ?? [])
          .filter((l: any) => !l.deletedAt)
          .map((l: any) => ({
            id: l.listenerUid ?? l.id ?? `${Date.now()}-${Math.random()}`,
            fullNameRu: l.fioRu ?? '',
            fullNameBy: l.fioBy ?? '',
            address: l.address ?? '',
            passport: l.passportNumber ?? '',
            passportIssueDate: isoToDisplay(l.passportIssueDate),
            passportIssuer: l.passportIssuedBy ?? '',
            externalEmail: l.email ?? '',
            status: 'not_started' as const,
          }));
        setListeners(restored);
        if (contract.groupId) setGroupId(contract.groupId);
        if (contract.consentGiven) setConsent(true);
        if (contract.uuid) setContractUuid(contract.uuid);
      })
      .catch(() => toast.showOnFailure('Не удалось загрузить черновик договора'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingContractId]);

  const updateForm = <K extends keyof ClientForm>(key: K, val: ClientForm[K]) => {
    if (key === 'unp') setImnsVkods(null);
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const updateEditing = <K extends keyof ListenerFullRow>(
    key: K,
    val: ListenerFullRow[K],
  ) =>
    setEditingListener((prev) => (prev ? { ...prev, [key]: val } : prev));

  const openAddListener = () => {
    if (!contractId) {
      toast.showOnFailure('Сначала создайте договор на шаге 1');
      return;
    }
    setEditingListener(emptyListener());
    setListenerModalOpen(true);
  };

  const openAddSelf = async () => {
    if (!contractId) {
      toast.showOnFailure('Сначала создайте договор на шаге 1');
      return;
    }
    let prefill: Partial<ListenerFullRow> = {};
    try {
      const profile: any = await api.get(ENDPOINT.PROFILE);
      const nameParts = [profile.firstName, profile.lastName].filter(Boolean);
      prefill = {
        externalEmail: profile.email ?? '',
        fullNameRu: nameParts.join(' '),
      };
    } catch {
      // profile unavailable — open empty modal
    }
    setEditingListener({ ...emptyListener(), ...prefill });
    setListenerModalOpen(true);
  };

  const openEditListener = (l: ListenerFullRow) => {
    setEditingListener({ ...l });
    setListenerModalOpen(true);
  };

  const closeListenerModal = () => {
    setListenerModalOpen(false);
    setEditingListener(null);
  };

  const openAddFromPrevious = async () => {
    if (!contractId) {
      toast.showOnFailure('Сначала создайте договор на шаге 1');
      return;
    }
    setPrevListenersOpen(true);
    setPrevListenersLoading(true);
    try {
      const rows = await api.getJson(ENDPOINT.LISTENERS_PREVIOUS);
      setPrevListeners(Array.isArray(rows) ? rows : []);
    } catch {
      toast.showOnFailure('Ошибка загрузки слушателей');
      setPrevListenersOpen(false);
    } finally {
      setPrevListenersLoading(false);
    }
  };

  const selectPreviousListener = (prev: PreviousListener) => {
    const isoToDisplay = (iso: string | null) =>
      iso ? iso.split('-').reverse().join('.') : '';
    setEditingListener({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      fullNameRu: prev.fioRu ?? '',
      fullNameBy: prev.fioBy ?? '',
      address: prev.address ?? '',
      passport: prev.passportNumber ?? '',
      passportIssueDate: isoToDisplay(prev.passportIssueDate),
      passportIssuer: prev.passportIssuedBy ?? '',
      externalEmail: prev.email ?? '',
      status: 'not_started',
    });
    setPrevListenersOpen(false);
    setListenerModalOpen(true);
  };

  const saveListener = async () => {
    if (!editingListener) return;

    if (!editingListener.fullNameRu?.trim()) {
      toast.showOnFailure('ФИО на русском обязательно');
      return;
    }
    if (!editingListener.fullNameBy?.trim()) {
      toast.showOnFailure('ФИО на белорусском обязательно');
      return;
    }
    if (!editingListener.passport?.trim()) {
      toast.showOnFailure('Серия и номер паспорта обязательны');
      return;
    }
    if (!editingListener.passportIssueDate?.trim()) {
      toast.showOnFailure('Дата выдачи паспорта обязательна');
      return;
    }
    if (!editingListener.passportIssuer?.trim()) {
      toast.showOnFailure('Кем выдан паспорт — обязательное поле');
      return;
    }
    if (!editingListener.externalEmail?.trim()) {
      toast.showOnFailure('E-mail для доступа обязателен');
      return;
    }

    const isEdit = listeners.some((l) => l.id === editingListener.id);

    const passportIssueDateIso = editingListener.passportIssueDate
      ? editingListener.passportIssueDate.split('.').reverse().join('-')
      : null;
    const apiBody = {
      fioRu: editingListener.fullNameRu,
      fioBy: editingListener.fullNameBy || null,
      address: editingListener.address || null,
      passportNumber: editingListener.passport || null,
      passportIssueDate: passportIssueDateIso,
      passportIssuedBy: editingListener.passportIssuer || null,
      email: editingListener.externalEmail || null,
    };

    if (isEdit) {
      if (contractId) {
        try {
          await api.patch(
            `${ENDPOINT.CONTRACTS}/${contractId}/listeners/${editingListener.id}`,
            apiBody
          );
        } catch {
          toast.showOnFailure('Ошибка обновления слушателя');
          return;
        }
      }
      setListeners((prev) =>
        prev.map((l) => (l.id === editingListener.id ? editingListener : l))
      );
    } else {
      if (contractId) {
        try {
          const response = await api.postJson(
            `${ENDPOINT.CONTRACTS}/${contractId}/listeners`,
            apiBody
          );
          const newListener: ListenerFullRow = {
            ...editingListener,
            id: response.listenerUid,
          };
          setListeners((prev) => [...prev, newListener]);
        } catch {
          toast.showOnFailure('Ошибка добавления слушателя');
          return;
        }
      } else {
        setListeners((prev) => [...prev, editingListener]);
      }
    }
    closeListenerModal();
  };

  const removeListener = async (lid: string) => {
    if (contractId) {
      try {
        await api.delete(`${ENDPOINT.CONTRACTS}/${contractId}/listeners/${lid}`);
      } catch {
        toast.showOnFailure('Ошибка удаления слушателя');
        return;
      }
    }
    setListeners((prev) => prev.filter((l) => l.id !== lid));
  };

  const isEditMode =
    !!editingListener && listeners.some((l) => l.id === editingListener.id);

  const totalPrice = useMemo(
    () => `${(priceKopecks / 100 * listeners.length).toFixed(0)} BYN`,
    [priceKopecks, listeners.length]
  );

  const selectedGroup = groups.find((g) => g.id === groupId);

  const validateStep1 = (): string | null => {
    if (clientType === 'legal') {
      if (!form.unp.trim()) return 'Введите УНП';
      if (!form.organization.trim()) return 'Введите наименование организации';
      if (!form.legalAddress.trim()) return 'Введите юридический адрес';
      if (!form.signerName.trim()) return 'Введите ФИО подписанта';
      if (!form.signerPosition.trim()) return 'Введите должность подписанта';
      if (!form.signingBasis.trim()) return 'Введите основание подписания';
    }
    if (clientType === 'ip') {
      if (!form.unp.trim()) return 'Введите УНП';
      if (!form.organization.trim()) return 'Введите наименование ИП';
      if (!form.postalAddress.trim()) return 'Введите почтовый адрес';
    }
    if (clientType === 'individual') {
      if (!form.individualFullName.trim()) return 'Введите ФИО';
      if (!form.passportNumber.trim()) return 'Введите номер паспорта';
      if (!form.passportIssueDate.trim()) return 'Введите дату выдачи паспорта';
      if (!form.passportIssuer.trim()) return 'Введите орган выдачи паспорта';
      if (!form.postalAddress.trim()) return 'Введите почтовый адрес';
    }
    if (!form.phone.trim()) return 'Введите телефон';
    if (!form.email.trim()) return 'Введите e-mail';
    return null;
  };

  const handleImnsLookup = async () => {
    if (!form.unp.trim()) {
      toast.showOnFailure('Введите УНП перед запросом к ИМНС');
      return;
    }
    try {
      const result = await api.getJson(`${ENDPOINT.CLIENTS_IMNS}/${form.unp.trim()}`);
      if (result.manualEntry) {
        toast.showOnFailure('API ИМНС временно недоступен — введите данные вручную');
        return;
      }
      if (result.vnaimp) updateForm('organization', result.vnaimp);
      if (result.vpadres) updateForm('legalAddress', result.vpadres);
      setImnsVkods(result.vkods ?? null);
    } catch (err: any) {
      if (err?.errorCode === 'IMNS_INACTIVE_UNP') {
        toast.showOnFailure('УНП недействителен — организация не является действующей');
      } else {
        toast.showOnFailure('Ошибка запроса к ИМНС — введите данные вручную');
      }
    }
  };

  const handleViewContract = async () => {
    if (!contractUuid) return;
    try {
      const html = await api.getText(`${ENDPOINT.CONTRACTS}/${contractUuid}`);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.showOnFailure('Не удалось загрузить договор');
    }
  };

  const goNext = async () => {
    if (step === 1) {
      if (contractId) {
        setStep(2);
        return;
      }
      const validationError = validateStep1();
      if (validationError) {
        toast.showOnFailure(validationError);
        return;
      }
      try {
        const result = await createContract({
          courseId: id,
          groupId: groupId || null,
          consentGiven: consent,
          clientType,
          manuallyEntered: clientType === 'individual' || !imnsVkods,
          vkods: imnsVkods,
          form,
        });
        setContractId(result.contractId);
        setContractUuid(result.contractUuid);
        setStep(2);
      } catch {
        // toast already shown by useCreateContract
      }
      return;
    }
    if (step === 2 && listeners.length === 0) {
      toast.showOnFailure('Добавьте хотя бы одного слушателя');
      return;
    }
    if (step < 4) setStep(step + 1);
    else history.push(APP_URL.MY_COURSES);
  };

  const goBack = () => {
    if (step === 1) history.push(APP_URL.COURSES);
    else setStep(step - 1);
  };

  const handlePay = async () => {
    if (!contractId) return;
    try {
      await api.patch(`${ENDPOINT.CONTRACTS}/${contractId}`, { consentGiven: true });
      const submitResult = await submitContract(contractId);
      setContractNumber(submitResult.contractNumber);

      let paymentResult;
      try {
        const backendMethod = PAYMENT_METHOD_MAP[paymentMethod];
        paymentResult = await api.postJson(
          `${ENDPOINT.CONTRACTS}/${contractId}/payments`,
          { method: backendMethod }
        );
      } catch {
        toast.showOnFailure('Ошибка инициализации оплаты');
        return;
      }

      if (paymentMethod === 'card') {
        const formUrl = paymentResult?.result?.formUrl;
        if (formUrl) {
          window.location.href = formUrl;
          return;
        }
        toast.showOnFailure('Не удалось получить ссылку на оплату');
        return;
      }

      setStep(4);
    } catch {
      // toast already shown by useSubmitContract
    }
  };

  return (
    <PageWrapper>
      <div className={css.Wrapper}>
        <h1 className={css.Title}>Оформление договора</h1>
        <p className={css.Subtitle}>Курс: {courseTitle}</p>

        <Stepper step={step} />

        {step === 1 && (
          <ClientDataStep
            clientType={clientType}
            onClientTypeChange={setClientType}
            allowedClientTypes={allowedClientTypes}
            form={form}
            updateForm={updateForm}
            prevRequisitesId={prevRequisitesId}
            onPrevRequisitesChange={setPrevRequisitesId}
            onImnsLookup={handleImnsLookup}
          />
        )}

        {step === 2 && (
          <ListenersStep
            courseTitle={courseTitle}
            courseFormat={courseFormat}
            courseDuration={courseDuration}
            clientType={clientType}
            form={form}
            listeners={listeners}
            onAdd={openAddListener}
            onAddSelf={openAddSelf}
            onAddFromPrevious={openAddFromPrevious}
            onEdit={openEditListener}
            onRemove={removeListener}
          />
        )}

        {step === 3 && (
          <GroupAndPaymentStep
            groups={groups}
            courseTitle={courseTitle}
            contractNumber={contractNumber ?? ''}
            listenersCount={listeners.length}
            pricePerStudent={`${Math.round(priceKopecks / 100)} BYN / слушатель`}
            totalPrice={totalPrice}
            groupId={groupId}
            onGroupChange={setGroupId}
            consent={consent}
            onConsentChange={setConsent}
            paymentMethod={paymentMethod}
            onPaymentMethodChange={setPaymentMethod}
            onPay={handlePay}
            onViewContract={handleViewContract}
          />
        )}

        {step === 4 && (
          <DoneStep
            contractNumber={contractNumber ?? '—'}
            group={selectedGroup}
            email={form.email}
            onMyCourses={() => history.push(APP_URL.MY_COURSES)}
            onCoursesList={() => history.push(APP_URL.COURSES)}
          />
        )}

        {step !== 3 && step !== 4 && (
          <StepFooter onBack={goBack} onNext={goNext} />
        )}

        {step === 3 && <StepFooter onBack={goBack} hideNext borderless />}
      </div>

      <ListenerModal
        open={listenerModalOpen}
        isEditMode={isEditMode}
        listener={editingListener}
        onChange={updateEditing}
        onClose={closeListenerModal}
        onSave={saveListener}
      />

      <PreviousListenersModal
        open={prevListenersOpen}
        loading={prevListenersLoading}
        listeners={prevListeners}
        onSelect={selectPreviousListener}
        onClose={() => setPrevListenersOpen(false)}
      />
    </PageWrapper>
  );
};

export default CourseEnrollment;
